import React from 'react';
import { CTASection } from '../../components/CTASection';
import { Car, CheckCircle, Sparkles, Shield, Zap, TrendingUp, DollarSign, Users } from 'lucide-react';
export function VehicleMagnets() {
  const benefits = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-1962" />,
    title: 'Custom Designs',
    description: 'Tailor your magnets to showcase your logo, contact info, and any message you want to convey'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-1963" />,
    title: 'Easy Application',
    description: 'Simply stick them on and remove them whenever you like—no sticky residue, just great visibility'
  }, {
    icon: <Shield className="w-6 h-6" data-id="element-1964" />,
    title: 'Durable Quality',
    description: 'Weather-resistant materials designed to last and look great in any environment'
  }, {
    icon: <TrendingUp className="w-6 h-6" data-id="element-1965" />,
    title: 'Cost-Effective Marketing',
    description: 'Turn your daily commute into advertising opportunities—no recurring costs'
  }];
  const advantages = [{
    icon: '🚗',
    title: 'Mobile Billboard',
    description: 'Your vehicle becomes a moving advertisement reaching thousands of potential customers'
  }, {
    icon: '💰',
    title: 'Affordable Advertising',
    description: 'One-time investment with unlimited impressions—no monthly fees or subscriptions'
  }, {
    icon: '🔄',
    title: 'Removable & Reusable',
    description: 'Take them off when needed, reuse them on different vehicles, or update your design'
  }, {
    icon: '🎯',
    title: 'Local Targeting',
    description: 'Reach customers in your service area as you drive through neighborhoods and communities'
  }, {
    icon: '⚡',
    title: 'Quick Setup',
    description: 'No installation required—apply in seconds and start advertising immediately'
  }, {
    icon: '🛡️',
    title: 'Vehicle Protection',
    description: "Magnets won't damage your paint and can be removed without leaving marks"
  }];
  const idealFor = ['Service businesses (plumbers, electricians, HVAC)', 'Delivery services', 'Real estate agents', 'Contractors & construction', 'Mobile pet grooming', 'Food trucks & catering', 'Cleaning services', 'Landscaping companies', 'Home healthcare providers', 'Sales representatives'];
  const features = ['Full-color printing', 'Custom sizes available', 'UV-resistant inks', 'Strong magnetic hold', 'Weather & fade resistant', 'Easy on, easy off', 'No paint damage', 'Reusable design'];
  return <div className="flex flex-col w-full" data-id="element-1966">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1967">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1968">
          <div className="flex justify-center mb-6" data-id="element-1969">
            <Car className="w-16 h-16 text-white" data-id="element-1970" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1971">
            Vehicle Magnets - Business Advertising
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1972">
            Make your brand unmissable on the go! Turn your vehicle into a
            mobile billboard with vibrant vehicle magnets that drive your
            business forward.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1973">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1974">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1975">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1976">
              Drive Your Business Forward!
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1977">
              <p data-id="element-1978">
                Turn your vehicle into a mobile billboard with our vibrant
                vehicle magnets! Perfect for promoting your business while
                you're on the road, our magnets are an easy and effective way to
                boost your brand visibility. Every mile you drive becomes an
                advertising opportunity, reaching thousands of potential
                customers in your local area.
              </p>
              <p data-id="element-1979">
                Whether you're a service professional, delivery driver, or small
                business owner, vehicle magnets offer a cost-effective marketing
                solution that works 24/7. With custom designs, durable
                materials, and easy application, you can start advertising your
                business immediately—no installation required!
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1980">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1981">
                <div className="flex items-start gap-4" data-id="element-1982">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1983">
                    {benefit.icon}
                  </div>
                  <div data-id="element-1984">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1985">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1986">{benefit.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Advantages */}
          <div className="mb-16" data-id="element-1987">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1988">
              Why Vehicle Magnets Work
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1989">
              {advantages.map((advantage, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-teal transition-colors text-center" data-id="element-1990">
                  <div className="text-4xl mb-4" data-id="element-1991">{advantage.icon}</div>
                  <h3 className="text-lg font-bold text-wh-teal mb-2" data-id="element-1992">
                    {advantage.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1993">
                    {advantage.description}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Features */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1994">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1995">
              Premium Quality Magnets
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-1996">
              {features.map((feature, index) => <div key={index} className="flex items-center gap-3" data-id="element-1997">
                  <CheckCircle className="w-6 h-6 text-wh-yellow flex-shrink-0" data-id="element-1998" />
                  <span className="text-lg" data-id="element-1999">{feature}</span>
                </div>)}
            </div>
          </div>

          {/* Ideal For */}
          <div className="mb-16" data-id="element-2000">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-2001">
              Perfect for These Businesses
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto" data-id="element-2002">
              Vehicle magnets are ideal for any business that uses vehicles for
              work or travel. Here are some of the industries we serve:
            </p>
            <div className="bg-gray-50 rounded-2xl p-12" data-id="element-2003">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-2004">
                {idealFor.map((business, index) => <div key={index} className="flex items-center gap-3" data-id="element-2005">
                    <CheckCircle className="w-5 h-5 text-wh-pink flex-shrink-0" data-id="element-2006" />
                    <span className="text-gray-700" data-id="element-2007">{business}</span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* ROI Highlight */}
          <div className="bg-gradient-to-br from-wh-yellow to-yellow-500 rounded-2xl p-12 mb-16" data-id="element-2008">
            <div className="max-w-3xl mx-auto" data-id="element-2009">
              <div className="flex items-start gap-4 mb-6" data-id="element-2010">
                <DollarSign className="w-12 h-12 text-gray-900 flex-shrink-0" data-id="element-2011" />
                <div data-id="element-2012">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2013">
                    Maximize Your Marketing ROI
                  </h2>
                  <p className="text-xl text-gray-800 mb-4" data-id="element-2014">
                    Vehicle magnets offer one of the best returns on investment
                    in advertising. With a one-time cost and unlimited
                    impressions, you're getting continuous exposure without
                    recurring fees.
                  </p>
                  <ul className="space-y-2 text-lg text-gray-700" data-id="element-2015">
                    <li className="flex items-start gap-2" data-id="element-2016">
                      <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" data-id="element-2017" />
                      <span data-id="element-2018">
                        Reach 30,000-70,000 impressions per vehicle per year
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-2019">
                      <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" data-id="element-2020" />
                      <span data-id="element-2021">
                        Cost per impression: pennies compared to traditional
                        advertising
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-2022">
                      <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" data-id="element-2023" />
                      <span data-id="element-2024">Works 24/7 wherever your vehicle goes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Process */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-2025">
            <div className="bg-white rounded-xl p-8 border-2 border-wh-pink text-center" data-id="element-2026">
              <div className="w-16 h-16 bg-wh-pink rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-2027">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2028">
                Design Your Magnet
              </h3>
              <p className="text-gray-700" data-id="element-2029">
                Work with our team to create a design that showcases your brand
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-teal text-center" data-id="element-2030">
              <div className="w-16 h-16 bg-wh-teal rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-2031">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2032">
                We Print & Produce
              </h3>
              <p className="text-gray-700" data-id="element-2033">
                High-quality printing on durable, weather-resistant magnetic
                material
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-yellow text-center" data-id="element-2034">
              <div className="w-16 h-16 bg-wh-yellow rounded-full flex items-center justify-center text-gray-900 text-2xl font-bold mx-auto mb-4" data-id="element-2035">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2036">
                Apply & Advertise
              </h3>
              <p className="text-gray-700" data-id="element-2037">
                Stick them on your vehicle and start turning heads immediately
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white" data-id="element-2038">
            <Car className="w-16 h-16 mx-auto mb-6" data-id="element-2039" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2040">
              Ready to Turn Heads and Attract Customers?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-2041">
              Contact us now to design your custom vehicle magnets and watch
              your business thrive on the go! Every mile becomes a marketing
              opportunity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2042">
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-2043">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-2044">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-2045" />
    </div>;
}